import type { SocialPlatformId } from '../../config/platformCapabilities';

export type WizardStepId =
  | 'overview'
  | 'requirements'
  | 'create_app'
  | 'configure_app'
  | 'enable_apis'
  | 'oauth'
  | 'permissions'
  | 'select_pages'
  | 'webhooks'
  | 'validate'
  | 'test_message'
  | 'test_receive'
  | 'test_comment'
  | 'test_publish'
  | 'test_analytics'
  | 'unsupported';

export interface WizardStep {
  id: WizardStepId;
  title: string;
  description: string;
  checklist: string[];
  helpUrl?: string;
  optional?: boolean;
}

export interface PlatformSetupGuide {
  platformId: SocialPlatformId;
  label: string;
  developerPortalUrl: string;
  overview: string;
  requirements: string[];
  beforeYouStart?: string[];
  unsupportedFeatures: string[];
  steps: WizardStep[];
  validationSteps?: string[];
  reconnectSteps?: string[];
  healthMonitoring?: string[];
  faq: { q: string; a: string }[];
  commonErrors: { error: string; cause: string; fix: string }[];
}

const metaBase = 'https://developers.facebook.com';

export const PLATFORM_SETUP_GUIDES: Record<SocialPlatformId, PlatformSetupGuide> = {
  facebook: {
    platformId: 'facebook',
    label: 'Facebook Pages',
    developerPortalUrl: metaBase,
    overview: 'Connect your Facebook Page to manage messages, comments, reviews, and publishing from one place.',
    requirements: [
      'A Facebook Page for your business (not a personal profile)',
      'Access to Meta Business Suite (recommended)',
      'Permission to connect apps to your Page',
    ],
    beforeYouStart: [
      'Use a computer — setup is easier on desktop.',
      'You need to be an admin on the Facebook Page.',
      'Plan which Page you want customers to message.',
    ],
    validationSteps: [
      'Connection Center shows “Connected” with green health.',
      'Run Diagnostics — all tests should pass.',
      'Reply to a test comment from Comments Center.',
    ],
    reconnectSteps: [
      'Connection Center → your Page → Refresh token.',
      'If problems continue, run the setup wizard again.',
    ],
    healthMonitoring: [
      'Check Connection Center weekly.',
      'Watch My Work for “Connection issue” alerts.',
    ],
    unsupportedFeatures: [],
    steps: [
      { id: 'create_app', title: 'Register your business app', description: 'Your IT partner can do this once for your company.', checklist: ['Open Meta for Business partners site', 'Create a Business app', 'Save App ID and App Secret in ERP Settings → Integrations'] },
      { id: 'configure_app', title: 'Turn on login and notifications', description: 'Allow the ERP to sign in and receive customer messages.', checklist: ['Enable Facebook Login', 'Copy the redirect URL from ERP into Meta', 'Save the verify code from ERP into Meta'] },
      { id: 'enable_apis', title: 'Allow Page features', description: 'Posts, messages, and insights for your Page.', checklist: ['Allow posting to the Page', 'Allow reading and replying to messages', 'Allow reading comments on posts'] },
      { id: 'oauth', title: 'Connect your Facebook Page', description: 'Link the Page you want customers to see.', checklist: ['Add channel in Connection Center', 'Click Connect', 'Pick the correct Page when asked'] },
      { id: 'permissions', title: 'Approve access', description: 'Click Allow on every permission screen.', checklist: ['Accept access to your Page', 'Accept messaging permission'] },
      { id: 'webhooks', title: 'Turn on live updates', description: 'So new messages appear in your inbox automatically.', checklist: ['Copy webhook address from ERP Settings', 'Paste into Meta app webhooks', 'Subscribe to messages and comments'] },
      { id: 'validate', title: 'Validate connection', description: 'Run automated tests in Connection Center.', checklist: ['Run token test', 'Run webhook test', 'Health score above 90%'] },
      { id: 'test_message', title: 'Test message', description: 'Send a test DM to your page.', checklist: ['Use “Simulate inbound” in Inbox or message your Page from Facebook', 'Reply from Unified Inbox'] },
      { id: 'test_comment', title: 'Test comment', description: 'Comment on a page post and reply from ERP.', checklist: ['Verify comment in Comments hub', 'Post a reply'] },
      { id: 'test_publish', title: 'Test publishing', description: 'Publish a draft post to the page.', checklist: ['Create post in Publishing Center', 'Publish and verify on Facebook'] },
    ],
    faq: [
      { q: 'Can I use a personal profile?', a: 'No — Facebook Pages API requires a Page.' },
      { q: 'Demo mode without Meta app?', a: 'Yes — use Demo Connect for training; live data needs real credentials.' },
    ],
    commonErrors: [
      { error: 'Webhook verification failed', cause: 'Verify token mismatch', fix: 'Copy token from Settings → Webhooks into Meta app' },
      { error: 'Token expired', cause: 'Long-lived token ended', fix: 'Use Refresh Token or Reconnect wizard' },
    ],
  },
  instagram: {
    platformId: 'instagram',
    label: 'Instagram Business',
    developerPortalUrl: metaBase,
    overview: 'Instagram Business uses your connected Facebook Page and Meta app.',
    requirements: ['Instagram Business or Creator account', 'Linked Facebook Page', 'Meta app with Instagram permissions'],
    unsupportedFeatures: ['Personal Instagram accounts'],
    steps: [
      { id: 'requirements', title: 'Instagram Business account', description: 'Convert to Business in Instagram app settings.', checklist: ['Professional account enabled', 'Linked to Facebook Page'] },
      { id: 'create_app', title: 'Meta app (shared)', description: 'Use the same Meta app as Facebook.', checklist: ['Instagram product added to app'] },
      { id: 'permissions', title: 'Permissions', description: 'instagram_manage_messages, instagram_content_publish.', checklist: ['Grant Instagram permissions in OAuth'] },
      { id: 'webhooks', title: 'Webhooks', description: 'Subscribe to Instagram messaging & comments.', checklist: ['Webhook URL configured', 'Test event received'] },
      { id: 'oauth', title: 'Connect channel', description: 'Add INSTAGRAM channel and connect via OAuth.', checklist: ['Channel connected', 'DM access confirmed'] },
      { id: 'test_message', title: 'Test DM', description: 'Retrieve and reply to a message.', checklist: ['Inbox shows Instagram thread'] },
      { id: 'test_comment', title: 'Test comment', description: 'Moderate comments on posts.', checklist: ['Comments hub filtered to Instagram'] },
      { id: 'test_publish', title: 'Test publishing', description: 'Publish feed post (stories need extra scopes).', checklist: ['Post published successfully'] },
      { id: 'test_analytics', title: 'Test analytics', description: 'Sync analytics from Analytics Center.', checklist: ['Refresh data shows Instagram metrics'] },
    ],
    faq: [{ q: 'Stories?', a: 'Story publishing requires additional Meta review — use Stories tab when enabled.' }],
    commonErrors: [{ error: 'No Instagram account', cause: 'Page not linked', fix: 'Link IG Business to Facebook Page in Meta Business Suite' }],
  },
  whatsapp: {
    platformId: 'whatsapp',
    label: 'WhatsApp Business',
    developerPortalUrl: metaBase,
    overview: 'WhatsApp Business for customer chats, approved templates, and your unified inbox — not for feed posts.',
    requirements: [
      'WhatsApp Business phone number (not personal WhatsApp)',
      'Meta Business Manager access',
      'Approved business display name',
    ],
    beforeYouStart: [
      'Complete Meta business verification if prompted.',
      'Have your business phone ready for a verification code.',
    ],
    validationSteps: [
      'Connection Center shows WhatsApp as Connected.',
      'Message your business number and see the thread in Unified Inbox.',
      'Reply from the inbox and confirm delivery on your phone.',
    ],
    unsupportedFeatures: ['Feed posts', 'Stories', 'Organic comments on posts'],
    steps: [
      { id: 'create_app', title: 'Register with Meta Business', description: 'One-time setup with Meta for your company.', checklist: ['Open Meta Business Suite', 'Add WhatsApp to your business portfolio', 'Note your business phone number'] },
      { id: 'configure_app', title: 'Verify your business number', description: 'Link the number customers will message.', checklist: ['Display name approved', 'Phone number verified', 'Messaging turned on'] },
      { id: 'webhooks', title: 'Turn on live message updates', description: 'So new chats appear automatically in your inbox.', checklist: ['Copy webhook address from ERP Settings', 'Paste into Meta WhatsApp settings', 'Use the verify code from ERP'] },
      { id: 'permissions', title: 'Save access in ERP', description: 'Your IT partner can paste access details from Meta into ERP Settings → Integrations.', checklist: ['Access saved in ERP', 'Ready to connect in Connection Center'] },
      { id: 'oauth', title: 'Connect WhatsApp', description: 'Add a WhatsApp channel in Connection Center and click Connect.', checklist: ['Status shows Connected', 'Health score is green'] },
      { id: 'test_message', title: 'Send a test reply', description: 'Confirm your team can reply to customers.', checklist: ['Open Unified Inbox', 'Reply to a test thread', 'Customer receives the message'] },
      { id: 'test_receive', title: 'Receive a customer message', description: 'Confirm messages appear in your inbox within one minute.', checklist: ['Send a WhatsApp to your business number', 'See the thread in Unified Inbox'], optional: true },
    ],
    faq: [{ q: 'Can I send bulk free-form messages?', a: 'Only within 24h session window; otherwise use approved templates.' }],
    commonErrors: [{ error: 'Template rejected', cause: 'Meta policy', fix: 'Edit template in WhatsApp Manager and resubmit' }],
  },
  linkedin: {
    platformId: 'linkedin',
    label: 'LinkedIn Pages',
    developerPortalUrl: 'https://www.linkedin.com/developers',
    overview: 'Publish posts and monitor comments on your Company Page. Direct messages are not supported in this workspace.',
    requirements: ['LinkedIn Company Page', 'LinkedIn Developer app', 'Marketing Developer Platform access (if required)'],
    unsupportedFeatures: ['DM inbox', 'Stories', 'WhatsApp-style broadcasts'],
    steps: [
      { id: 'create_app', title: 'Create LinkedIn app', description: 'Register app at LinkedIn Developers.', checklist: ['App created', 'Client ID and Secret saved in ERP'] },
      { id: 'oauth', title: 'OAuth connection', description: 'Connect company page via OAuth.', checklist: ['Redirect URL whitelisted', 'Channel connected'] },
      { id: 'permissions', title: 'Permissions', description: 'w_organization_social, r_organization_social.', checklist: ['Posting permission granted'] },
      { id: 'test_publish', title: 'Publishing validation', description: 'Publish test post.', checklist: ['Post visible on Company Page'] },
      { id: 'test_comment', title: 'Comment monitoring', description: 'Verify comments sync.', checklist: ['Comments appear in workspace'] },
      { id: 'test_analytics', title: 'Analytics validation', description: 'Sync page analytics.', checklist: ['Analytics refresh succeeds'] },
    ],
    faq: [{ q: 'Why no inbox?', a: 'LinkedIn messaging API is restricted — ERP focuses on Page content and comments.' }],
    commonErrors: [{ error: '403 on publish', cause: 'Missing scope', fix: 'Re-auth with w_organization_social' }],
  },
  x: {
    platformId: 'x',
    label: 'X (Twitter)',
    developerPortalUrl: 'https://developer.twitter.com',
    overview: 'Publish posts, monitor mentions and replies. No unified DM inbox in this integration tier.',
    requirements: ['X Developer account', 'Project with Elevated or Pro access', 'OAuth 2.0 app'],
    unsupportedFeatures: ['DM inbox', 'Google-style reviews'],
    steps: [
      { id: 'create_app', title: 'Create X app', description: 'Create project and app in Developer Portal.', checklist: ['API keys stored in ERP Settings'] },
      { id: 'oauth', title: 'Connect OAuth 2.0', description: 'Authorize posting and read access.', checklist: ['Channel connected'] },
      { id: 'test_publish', title: 'Test post', description: 'Publish from Publishing Center.', checklist: ['Post appears on profile'] },
      { id: 'test_comment', title: 'Test replies', description: 'Monitor mentions and replies.', checklist: ['Listening or inbox filters show activity'] },
    ],
    faq: [],
    commonErrors: [{ error: 'Rate limited', cause: 'API tier limits', fix: 'Upgrade X API tier or reduce publish frequency' }],
  },
  youtube: {
    platformId: 'youtube',
    label: 'YouTube',
    developerPortalUrl: 'https://console.cloud.google.com',
    overview: 'Manage videos, moderate comments, and view analytics. No DM inbox.',
    requirements: ['Google Cloud project', 'YouTube Data API enabled', 'OAuth consent screen configured'],
    unsupportedFeatures: ['DM inbox', 'Broadcasts'],
    steps: [
      { id: 'create_app', title: 'Google Cloud project', description: 'Create project and enable YouTube Data API v3.', checklist: ['API enabled', 'OAuth client created'] },
      { id: 'oauth', title: 'OAuth setup', description: 'Connect YouTube channel.', checklist: ['Channel authorized', 'Refresh token stored'] },
      { id: 'permissions', title: 'Permissions', description: 'youtube.force-ssl, youtube.upload (if publishing).', checklist: ['Scopes granted'] },
      { id: 'test_comment', title: 'Comment moderation', description: 'List and reply to video comments.', checklist: ['Comments visible in YouTube workspace'] },
      { id: 'test_analytics', title: 'Analytics access', description: 'Pull channel analytics.', checklist: ['Analytics sync OK'] },
    ],
    faq: [],
    commonErrors: [{ error: 'quotaExceeded', cause: 'API quota', fix: 'Request quota increase in Google Cloud Console' }],
  },
  google_business: {
    platformId: 'google_business',
    label: 'Google Business Profile',
    developerPortalUrl: 'https://developers.google.com/my-business',
    overview: 'Monitor and reply to reviews and view location insights.',
    requirements: ['Google Business Profile', 'Google Cloud OAuth', 'Business Profile API access'],
    unsupportedFeatures: ['Feed publishing', 'DM inbox', 'Stories'],
    steps: [
      { id: 'oauth', title: 'Google account', description: 'Sign in with profile owner account.', checklist: ['OAuth completed'] },
      { id: 'select_pages', title: 'Select locations', description: 'Choose business locations to manage.', checklist: ['Correct location selected'] },
      { id: 'test_comment', title: 'Review access', description: 'Load reviews in workspace.', checklist: ['Reviews listed', 'Reply posted'] },
      { id: 'test_analytics', title: 'Insights', description: 'Validate metrics sync.', checklist: ['Insights in Analytics Center'] },
    ],
    faq: [],
    commonErrors: [{ error: 'Permission denied', cause: 'Wrong Google account', fix: 'Reconnect with profile owner account' }],
  },
  telegram: {
    platformId: 'telegram',
    label: 'Telegram',
    developerPortalUrl: 'https://core.telegram.org/bots',
    overview: 'Connect a Telegram bot for customer conversations.',
    requirements: ['Telegram bot via @BotFather', 'Bot token'],
    unsupportedFeatures: ['Posts', 'Stories', 'Publishing'],
    steps: [
      { id: 'create_app', title: 'Create bot', description: 'Message @BotFather and create bot.', checklist: ['Bot token copied'] },
      { id: 'webhooks', title: 'Webhook setup', description: 'Set ERP webhook URL on bot.', checklist: ['setWebhook called', 'Test message received'] },
      { id: 'oauth', title: 'Connect in ERP', description: 'Paste bot token in channel setup.', checklist: ['Channel active'] },
      { id: 'test_message', title: 'Message validation', description: 'Send message to bot and reply.', checklist: ['Inbound in inbox', 'Reply delivered'] },
    ],
    faq: [],
    commonErrors: [{ error: '409 Conflict', cause: 'Webhook already set elsewhere', fix: 'Remove old webhook then set ERP URL' }],
  },
  threads: {
    platformId: 'threads',
    label: 'Threads',
    developerPortalUrl: metaBase,
    overview: 'Publish threads and monitor mentions — via Meta ecosystem.',
    requirements: ['Threads profile linked to Instagram', 'Meta app'],
    unsupportedFeatures: ['DM inbox'],
    steps: [
      { id: 'oauth', title: 'Connect via Meta', description: 'Use Meta app with Threads permissions.', checklist: ['Channel connected'] },
      { id: 'test_publish', title: 'Test post', description: 'Publish thread from ERP.', checklist: ['Post live on Threads'] },
    ],
    faq: [],
    commonErrors: [],
  },
  pinterest: {
    platformId: 'pinterest',
    label: 'Pinterest',
    developerPortalUrl: 'https://developers.pinterest.com',
    overview: 'Publish pins and monitor comments.',
    requirements: ['Pinterest Business account', 'Pinterest app'],
    unsupportedFeatures: ['DM inbox'],
    steps: [
      { id: 'create_app', title: 'Create Pinterest app', description: 'Register app and get credentials.', checklist: ['App ID in ERP Settings'] },
      { id: 'oauth', title: 'Connect', description: 'OAuth to business account.', checklist: ['Connected'] },
      { id: 'test_publish', title: 'Test pin', description: 'Publish from Publishing Center.', checklist: ['Pin created'] },
    ],
    faq: [],
    commonErrors: [],
  },
  tiktok: {
    platformId: 'tiktok',
    label: 'TikTok',
    developerPortalUrl: 'https://developers.tiktok.com',
    overview: 'Publish content and view analytics where API access is granted.',
    requirements: ['TikTok for Business', 'Developer app approval'],
    unsupportedFeatures: ['DM inbox (limited API)'],
    steps: [
      { id: 'create_app', title: 'TikTok developer app', description: 'Apply for Content Posting API.', checklist: ['App approved'] },
      { id: 'oauth', title: 'Connect', description: 'Authorize business account.', checklist: ['Token stored'] },
      { id: 'test_publish', title: 'Test publish', description: 'Post video or image.', checklist: ['Content published'] },
    ],
    faq: [],
    commonErrors: [],
  },
};

export function getSetupGuide(platformId: string): PlatformSetupGuide | undefined {
  const key = platformId.toLowerCase().replace(/-/g, '_') as SocialPlatformId;
  return PLATFORM_SETUP_GUIDES[key];
}
